import React, {useMemo} from 'react';
import { positionMenuByTrigger } from '../../utils/menuPosition';
import {useSelector, useDispatch} from 'react-redux';
import {ProgressBar, Button, Dropdown} from 'react-bootstrap';
import {
    setLoudnessFiles, setLoudnessProgress, setLoudnessIsProcessing, resetLoudnessFilePath, setLoudnessAutoAnalyze
} from '../../redux/slices/loudnessSlice';
import { useToast } from '../../contexts/ToastContext';
import { track } from '../../utils/analytics';
import { loadCloudPlaylistFiles } from '../../utils/cloudPlaylistTracks';
import { offerFixedCopiesCommit } from '../../utils/fixedCopiesCommit';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import {clearLogs, addLogs, updateOrAddLogs, addLogsLineBreak, removeLogsForFile, setLogs, clearPendingLogs} from '../../redux/slices/loudnessLogsSlice';
import {formatPath} from "../../utils/formatPath";
import { confirmLargeSet, countAudioFiles, SET_COST } from '../../utils/largeSetGuard';
import { setListGrouping, setListSort, setLoudnessFilterMin, setLoudnessFilterMax, setLoudnessLevel, setInputLoudnessLevel,
    setChangeLoudness, setAddLoudnessToFileNames, setBackupLoudnessOriginalFiles } from '../../redux/slices/loudnessSettingsSlice';
import { useDesignV2 } from '../../utils/designV2';
import ToolHeader, { PopoverRow } from '../v2/ToolHeader';
import ToggleSwitch from '../ToggleSwitch';
import { isFolderSource, groupRowsByFolder, sortTrackRowsByLabel, useDisplayLabel } from '../../utils/folderGrouping';
import LoudnessSettings from "../loudnessSettings";
import {supportedFormats} from "../../utils/supportedFileFormats";
import ProgressBarWithStop from "../ProgressBarWithStop.js";
import ToolRow from '../v2/ToolRow';
import LogStatusIcon from "../LogStatusIcon";
import { setLoudnessCancelRequested } from "../../redux/slices/loudnessSlice";
import { ReactComponent as AnalyzeFilesIcon } from '../../assets/icons/analyze-files.svg';
import { ReactComponent as MatchLoudnessIcon } from '../../assets/icons/match-loudness.svg';
import { ReactComponent as EditFilenamesIcon } from '../../assets/icons/edit-filenames.svg';
import {ReactComponent as DirectoryIcon } from '../../assets/icons/select-folder.svg';
import {ReactComponent as AudioFileIcon } from '../../assets/icons/audio-file.svg';
import {ReactComponent as WavFileIcon } from '../../assets/icons/wav-file.svg';
import {ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import {ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import {ReactComponent as StopIcon } from '../../assets/icons/stop.svg';
import {ReactComponent as ArrowUpIcon } from '../../assets/icons/arrow-up.svg';
import {ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down.svg';
import {ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import {ReactComponent as OpenIcon } from '../../assets/icons/open.svg';
import { addTrack, setAutoPlay, resetAudioList } from '../../redux/slices/musicPlayerSlice';
import RowActions from '../RowActions';
import BulkRowContextMenu from '../BulkRowContextMenu';
import AddToPlaylistModal from '../AddToPlaylistModal';
import useRowSelection from '../../hooks/useRowSelection';
import { parseTrackFromPath } from '../../utils/parseTrackFromPath';
import { waitRemaining } from '../../utils/minVisibleDelay';
import { statDigits } from '../../utils/statDigits';
import StatCount from '../StatCount';
import { useEnrichedTracks, getEnrichedParts } from '../../hooks/useEnrichedTracks';
import { usePlayerRowSync, localPathFromMusicSrc } from '../../hooks/usePlayerRowSync';
import { setLocalListContext } from '../../utils/localListContext';
import { resolveOrderedAudioFiles } from '../../utils/orderedAudioFiles';
import CoverPlayButton from '../CoverPlayButton';
import { batchProgressTicks } from '../../utils/progressBatcher';
import { useTierGate } from '../../hooks/useTierGate';
// Shared with the matcher so the warning threshold can't drift from the
// backend's skip rule (loudnessLimits is dependency-free for this reason).
import { needsExtremeBoost, matchGainDb } from '../../../backend/loudnessLimits';
import { useVisibleLoudnessRows, useReachableLoudness } from '../../hooks/useReachableLoudness';
import './SmartRecsTab.scss';

// Shared by the old summary card and the v2 header sentence.
function computeLoudnessStats(logs, loudnessLevel) {
        let totalAudio = 0;
        let matched = 0;
        let highest = -Infinity;
        let lowest = Infinity;
        let hasNumericLoudness = false;
        // Files a match would damage rather than fix — 'silent' (pinned at
        // ffmpeg's -70 gate floor, so unmeasurable) and 'too-quiet' (real but
        // needing an absurd boost). Both are surfaced as ONE stat; the row
        // badges say which.
        let unmatchable = 0;

        for (const log of logs) {
            // Pre-populated rows count toward the total immediately — the list
            // already shows them all, so the header shouldn't climb one-by-one.
            // (Rows the run never reaches are dropped at completion, so the
            // total may tick down slightly then.)
            if (log.status === 'pending') { totalAudio++; continue; }
            if (log.status === 'silent' || log.status === 'too-quiet' || log.status === 'at-ceiling') {
                unmatchable++;
                totalAudio++;
            }
            // 'too-quiet' carries a REAL measurement, so it still belongs in
            // the highest/lowest range; 'silent' has no number at all.
            if (log.status !== 'analyze-loudness' && log.status !== 'too-quiet' && log.status !== 'at-ceiling') continue;
            if (log.status === 'analyze-loudness') totalAudio++;
            // loudnessLevel may be a persisted string from the text input —
            // strict === against the numeric measurement always missed.
            if (typeof log.loudness === 'number' && log.loudness === Number(loudnessLevel)) matched++;
            if (typeof log.loudness === 'number') {
                hasNumericLoudness = true;
                if (log.loudness > highest) highest = log.loudness;
                if (log.loudness < lowest) lowest = log.loudness;
            }
        }
        return { totalAudio, matched, highest, lowest, hasNumericLoudness, unmatchable };
}

const LogSummaryStats = React.memo(({ logs, loudnessLevel, formatToNegativeDecimal, hiddenByFilter = 0 }) => {
    const stats = useMemo(() => computeLoudnessStats(logs, loudnessLevel), [logs, loudnessLevel]);

    // Every count sits in a slot as wide as the total (StatCount +
    // utils/statDigits.js) so the items after "Loudness Matched" stay put
    // while it climbs during a run; the LUFS readings reserve "-00.0 LUFS".
    const digits = statDigits(stats.totalAudio, stats.matched, stats.unmatchable, hiddenByFilter);
    return (
        <div className="changes-container log-entry">
            <span>
                <AudioFileIcon className='icon'/>
                Total Audio Files: <StatCount value={stats.totalAudio} digits={digits} align="start" />
            </span>
            <span>
                <MatchLoudnessIcon className='icon'/>
                Loudness Matched: <StatCount value={stats.matched} digits={digits} align="start" />
            </span>
            {stats.hasNumericLoudness && (
                <>
                    <span>
                        <ArrowUpIcon className='icon'/>
                        Highest: <StatCount value={formatToNegativeDecimal(stats.highest, true)} ghost="-00.0 LUFS" align="start" />
                    </span>
                    <span>
                        <ArrowDownIcon className='icon'/>
                        Lowest: <StatCount value={formatToNegativeDecimal(stats.lowest, true)} ghost="-00.0 LUFS" align="start" />
                    </span>
                </>
            )}
            {stats.unmatchable > 0 && (
                <span className="stat-unmatchable" title="Files a match can't help: silent or extremely quiet (boosting would amplify noise), or already peaking near full scale (can't go louder without clipping)">
                    <svg className="icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /></svg>
                    Can't Match: <StatCount value={stats.unmatchable} digits={digits} align="start" />
                </span>
            )}
            {hiddenByFilter > 0 && (
                <span className="stat-filtered-out" title="Match runs on the visible rows only — widen the LUFS filter to include these">
                    <svg className="icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                    Filtered Out: <StatCount value={hiddenByFilter} digits={digits} align="start" />
                </span>
            )}
        </div>
    );
});

// ---- Row rendering (module level, memoized) ----
// Same shape as CompatLogRow in FixMyTracksTab: one finished file used to
// re-render every row (the list was built inline in the tab). Each row is a
// memo component keyed by file path whose props are its own log object
// (Immer keeps untouched rows' identity across ticks), booleans derived for
// THIS row, the settings it reads, and a stable `api` that reads the tab's
// latest handlers through a ref.

function formatToNegativeDecimal(value, withUnit = false) {
    // Ensure the number is negative and has one decimal place
    if (typeof(value) !== 'number' || isNaN(value)) {
        return 'N/A'
    }
    let formatted = (-Math.abs(value)).toFixed(1);
    if (value === 0) {
        formatted = '-0.0';
    }
    return withUnit ? `${formatted} LUFS` : formatted;
}

const isTrackLogRow = (log) => !!(log?.filePath && log.status !== 'summary' && log.status !== 'separator' && log.status !== 'folder-header');

// DESIGN V2 row: play button, the name once, a 160px bar (measured vs the
// target, white tick at the target), the number right-aligned. Bar scale is
// -30..0 LUFS — everything a DJ library realistically lands in.
const V2_BAR_FLOOR = -30;
const v2BarPct = (lufs) => Math.max(0, Math.min(100, ((Number(lufs) - V2_BAR_FLOOR) / -V2_BAR_FLOOR) * 100));

function LoudnessRowV2({ log, enriched, selected, playing, ctxAt, loudnessLevel, api }) {
    const target = Number(loudnessLevel);
    const measured = typeof log.loudness === 'number' ? log.loudness : null;
    const atTarget = measured != null && Math.abs(measured - target) < 0.05;
    const status = log.status;
    const unmatchable = status === 'silent' || status === 'too-quiet' || status === 'at-ceiling';
    let num;
    let numClass = '';
    if (status === 'matching') { num = 'Matching…'; numClass = 'muted'; }
    else if (status === 'silent') { num = 'Silent'; numClass = 'muted'; }
    else if (status === 'file-not-found') { num = 'Not found'; numClass = 'bad'; }
    else if (status === 'error') { num = 'Error'; numClass = 'bad'; }
    else if (status === 'pending') { num = ''; numClass = 'pending'; }
    else if (measured != null) { num = formatToNegativeDecimal(measured); numClass = unmatchable ? 'muted' : (atTarget ? 'ok' : 'warn'); }
    else { num = '—'; numClass = 'muted'; }
    const barTitle = measured != null
        ? `${formatToNegativeDecimal(measured, true)} · target ${formatToNegativeDecimal(target, true)}${atTarget ? '' : ` · ${measured > target ? '' : '+'}${(target - measured).toFixed(1)} dB to match`}`
        : undefined;
    return (
        <ToolRow
            rowKey={log.filePath}
            className="v2-ld-row"
            status={status || ''}
            selected={selected}
            onClick={(e) => api.rowClick(log, e)}
            onContextMenu={(e) => api.rowContextMenu(log, e)}
            onDoubleClick={(e) => api.playClick(log, e)}
            cover={(
                <CoverPlayButton
                    filePath={log.filePath}
                    cover={enriched.cover}
                    isPlaying={playing}
                    onClick={(e) => playing ? api.pauseClick(e) : api.playClick(log, e)}
                />
            )}
            name={enriched.label}
            nameTitle={log.detail || log.filePath}
            note={log.skippedExisting && (
                <span className="log-note-pill" title={`A ${loudnessLevel} LUFS copy already exists — enable "Overwrite existing fixed files" in Settings to re-create it`}>Matched copy exists</span>
            )}
            detail={log.detail && (status === 'error' || unmatchable) ? log.detail : null}
            middle={(
                <span className={`v2-ld-bar${measured == null ? ' empty' : ''}`} title={barTitle} aria-hidden="true">
                    {measured != null && <i className={`v2-ld-fill ${unmatchable ? 'muted' : (atTarget ? 'ok' : 'warn')}`} style={{ width: `${v2BarPct(measured)}%` }} />}
                    {Number.isFinite(target) && <i className="v2-ld-tick" style={{ left: `${v2BarPct(target)}%` }} />}
                </span>
            )}
            number={(
                <span className={`v2-ld-num ${numClass}`} title={log.detail || undefined}>
                    {status === 'pending' ? <span className="loudness-badge pending-skeleton" title="Waiting to be analyzed">{' '}</span> : num}
                </span>
            )}
            menu={(
                <RowActions
                    track={{ title: enriched.title, artist: enriched.artist, filePath: log.filePath, artwork: enriched.cover || null, djpalId: enriched.djpalId || null }}
                    onAddToQueue={() => api.addToQueue(log, { stopPropagation() {} })}
                    onRemoveFromLibrary={() => api.removeFromList([log.filePath], 'menu')}
                    removeLabel="Remove from list"
                    openAtPoint={ctxAt}
                    desktopLike
                    analyticsFrom="match_loudness"
                />
            )}
        />
    );
}

const LoudnessLogRow = React.memo(function LoudnessLogRow({ log, enrichedMap, selected, playing, ctxAt, loudnessLevel, isLast, api, v2 = false }) {
    const isTrack = isTrackLogRow(log);
    const enriched = isTrack ? getEnrichedParts(enrichedMap, log.filePath) : null;
    if (v2 && isTrack) {
        return <LoudnessRowV2 log={log} enriched={enriched} selected={selected} playing={playing} ctxAt={ctxAt} loudnessLevel={loudnessLevel} api={api} />;
    }
    return (
        <div
            data-rowkey={isTrack ? log.filePath : undefined}
            className={`log-entry ${log && log.status ? log.status : ''} ${isTrack && selected ? 'selected-row' : ''}`}
            onClick={isTrack ? (e) => api.rowClick(log, e) : undefined}
            // Shift-click extends the selection — suppress the browser's
            // native shift-click text-selection so the two don't fight.
            onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
            onContextMenu={(e) => api.rowContextMenu(log, e)}
            onDoubleClick={(e) => api.playClick(log, e)}
        >
            {isTrack && (
                <CoverPlayButton
                    filePath={log.filePath}
                    cover={enriched.cover}
                    isPlaying={playing}
                    onClick={(e) => playing ? api.pauseClick(e) : api.playClick(log, e)}
                />
            )}
            <div className="log-text">
                {(log && log.msg || "").split('\n').map((line, i) => {
                    // Track rows: show the enriched "Artist - Title" (Mixpedia/ID3)
                    // we already know, falling back to the filename.
                    const displayLine = enriched
                        ? enriched.label
                        : line.replace(/\.[^/.]+$/, '');

                    return (
                    <div key={i}
                         className={`log-entry-wrapper ${i === 0 && line && line.startsWith("You've reached the") || line.startsWith("Go premium") ? "limit-reached" : ""}`}
                    >
                        {log.status === 'analyze-loudness' ? (
                            <>
                                <span className="loudness-badge">
                                    {formatToNegativeDecimal(log.loudness)}
                                </span>
                                <span className="log-message" title={log.detail || ''}>
                                    {displayLine}
                                    {log.skippedExisting && (
                                        <span className="log-note-pill" title={`A ${loudnessLevel} LUFS copy already exists — enable "Overwrite existing fixed files" in Settings to re-create it`}>Matched copy exists</span>
                                    )}
                                    {log.detail && <span className="error-detail"> — {log.detail}</span>}
                                </span>
                            </>
                        ) : log.status === 'matching' ? (
                            <>
                                <span className="loudness-badge matching">Matching…</span>
                                <span className="log-message">{displayLine}</span>
                            </>
                        ) : log.status === 'silent' ? (
                            <>
                                <span className="loudness-badge unmatchable" title="Nothing measurable above the silence threshold — loudness can't be matched">Silent</span>
                                <span className="log-message" title={log.detail || ''}>{displayLine}</span>
                            </>
                        ) : (log.status === 'too-quiet' || log.status === 'at-ceiling') ? (
                            <>
                                <span className="loudness-badge unmatchable" title={log.detail || ''}>
                                    {formatToNegativeDecimal(log.loudness)}
                                </span>
                                <span className="log-message" title={log.detail || ''}>
                                    {displayLine}
                                    {log.detail && <span className="error-detail"> — {log.detail}</span>}
                                </span>
                            </>
                        ) : log.status === 'file-not-found' ? (
                            <>
                                <span className="loudness-badge not-found" title="File not found at original path">Not Found</span>
                                <span className="log-message not-found" title={`File not found: ${log.filePath}`}>{displayLine}</span>
                            </>
                        ) : log.status === 'error' ? (
                            <>
                                <span className="loudness-badge error-badge" title={log.detail || 'File could not be processed'}>Error</span>
                                <span className="log-message" title={log.detail || ''}>
                                    {displayLine}
                                    {log.detail && <span className="error-detail"> — {log.detail}</span>}
                                </span>
                            </>
                        ) : log.status === 'pending' ? (
                            <>
                                <span className="loudness-badge pending-skeleton" title="Waiting to be analyzed">{' '}</span>
                                <span className="log-message">{displayLine}</span>
                            </>
                        ) : (
                            <span className="log-message">{i === 0 && <LogStatusIcon icon={log.icon} />}{line}</span>
                        )}
                        {!isLast && log && log.msg && i < log.msg.split('\n').length - 1 && <br />}
                    </div>
                    );
                })}
            </div>
            {isTrack && (
                <RowActions
                    track={{ title: enriched.title, artist: enriched.artist, filePath: log.filePath, artwork: enriched.cover || null, djpalId: enriched.djpalId || null }}
                    onAddToQueue={() => api.addToQueue(log, { stopPropagation() {} })}
                    onRemoveFromLibrary={() => api.removeFromList([log.filePath], 'menu')}
                    removeLabel="Remove from list"
                    openAtPoint={ctxAt}
                    desktopLike
                    analyticsFrom="match_loudness"
                />
            )}
        </div>
    );
});

const MatchLoudnessTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    // Real title/artist/cover for already-analyzed local files (see FixMyTracksTab).
    const enrichedMap = useEnrichedTracks();
    const { canAccess, requireUpgrade } = useTierGate('loudness-match');

    // Local state for playback control
    const [currentlyPlayingPath, setCurrentlyPlayingPath] = React.useState(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    // Reconcile the row highlight with the bottom player: pause/stop flips the
    // icon back, and list auto-advance moves the highlight to the next row.
    usePlayerRowSync(currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath);

    // Right-click menus: single-row opens the row's shared TrackContextMenu at
    // the cursor (identical to its ⋯ menu); a right-click inside a multi-row
    // selection opens the bulk menu.
    const [rowCtx, setRowCtx] = React.useState(null);   // { key, x, y, nonce }
    const [bulkCtx, setBulkCtx] = React.useState(null); // { x, y }
    const ctxNonceRef = React.useRef(0);
    // Bulk "Add N Tracks to Playlist" picker targets.
    const [bulkPlaylistTargets, setBulkPlaylistTargets] = React.useState([]);

    // Drag and drop state
    const [isDragging, setIsDragging] = React.useState(false);

    // Source identity guard. Selecting a playlist resolves ASYNCHRONOUSLY
    // (cloud rows -> local files); an Analyze click racing that resolution
    // used to run the PREVIOUS source while the header already showed the
    // playlist. Every source change bumps the epoch; a run aborts at each
    // await if the source changed under it, and the run buttons are disabled
    // while a selection is still resolving.
    const sourceEpochRef = React.useRef(0);
    const [sourceResolving, setSourceResolving] = React.useState(false);

    // Playlist dropdown state
    const [selectedPlaylistName, setSelectedPlaylistName] = React.useState(null);
    // Source playlist identity ({source, id, rowIdsByPath}) — lets the
    // "Use fixed files" commit swap loudness copies into the playlist too.
    const [selectedPlaylistRef, setSelectedPlaylistRef] = React.useState(null);
    const [playlistDropdownOpen, setPlaylistDropdownOpen] = React.useState(false);
    const [playlistList, setPlaylistList] = React.useState([]);
    const [playlistsLoading, setPlaylistsLoading] = React.useState(false);
    const [playlistSearch, setPlaylistSearch] = React.useState('');
    const [playlistDropdownPos, setPlaylistDropdownPos] = React.useState({ top: 0, left: 0, width: 250 });
    const playlistDropdownRef = React.useRef(null);
    const playlistBtnRef = React.useRef(null);
    // Flip the "Open ▸ Playlist" picker above its button when there's no room below.
    React.useLayoutEffect(() => {
        if (!playlistDropdownOpen || !playlistDropdownRef.current || !playlistBtnRef.current) return;
        const rect = playlistBtnRef.current.getBoundingClientRect();
        const { top } = positionMenuByTrigger(playlistDropdownRef.current, rect, { gap: 2, align: null });
        setPlaylistDropdownPos(prev => (prev.top === top ? prev : { ...prev, top }));
    }, [playlistDropdownOpen, playlistList, playlistSearch, playlistsLoading]);
    const playlistSearchRef = React.useRef(null);

    const {
        loudnessFiles, loudnessIsProcessing, loudnessProgress, cancelRequested, autoAnalyze
    } = useSelector((state) => state.loudness);
    const logs = useSelector((state) => state.loudnessLogs.logs);
    const { loudnessLevel, changeLoudness, addLoudnessToFileNames, backupLoudnessOriginalFiles, listGrouping, listSort,
        loudnessFilterMin, loudnessFilterMax, inputLoudnessLevel }
        = useSelector((state) => state.loudnessSettings);
    // DESIGN V2 — structural changes (tool header, filter chips, bar rows) are
    // gated on this; the old structure is untouched while it is off.
    const v2 = useDesignV2();
    const v2AutoArmedRef = React.useRef(null);  // DESIGN V2: one auto-analyze per loaded source

    // Signed-in users' playlists live in the cloud — the picker lists those
    // and resolves their tracks to local files (utils/cloudPlaylistTracks).
    const accessToken = useSelector((state) => state.auth.accessToken);
    const useServer = !!accessToken;
    const {
        enableRecursive,
        overwriteExistingFixedFiles,
        logOnlyChanges,
        concurrencyLevel,
    } = useSelector((state) => state.settings);

    // The log list as rendered (LUFS range applied). This IS the Match run's
    // scope: what you see is what runs. Cmd+F find is highlight-only and
    // deliberately never narrows this list. Shared with the settings panel
    // (useReachableLoudness) so its ceiling hint reasons about the same rows.
    const filteredLogs = useVisibleLoudnessRows();

    // Visible-scope counts for the button label, hint line and pre-run
    // confirm. Row count semantics (NOT files-needing-matching) — same as the
    // button always showed.
    const matchableCount = useMemo(() => logs.filter(l => l?.status === 'analyze-loudness').length, [logs]);
    const visibleMatchableCount = useMemo(() => filteredLogs.filter(l => l?.status === 'analyze-loudness').length, [filteredLogs]);
    const hiddenMatchableCount = matchableCount - visibleMatchableCount;
    // Why the Match button is disabled, spelled out under the toolbar — a
    // `title` on a disabled button never shows (no hover events), so the user
    // saw a dead button after Analyze with no explanation.
    const matchDisabledReason = (loudnessFiles.length === 0 || loudnessIsProcessing || sourceResolving) ? null
        : (!changeLoudness && !addLoudnessToFileNames)
            ? 'Nothing to apply — turn on "Match Loudness to Chosen Value" or "Add Loudness to Filenames" in Settings.'
            : (matchableCount > 0 && visibleMatchableCount === 0)
                ? `All ${matchableCount} analyzed track${matchableCount === 1 ? ' is' : 's are'} hidden by the LUFS filter — widen it to match them.`
                : null;

    // The loudest level EVERY track in scope can reach without clipping — the
    // settings panel shows it as a suggestion while the target is being
    // chosen, so by the time Run is pressed the user has already seen (and
    // usually taken) it. No modal here: the consequence of aiming above it is
    // stated right under the target input, and the backend caps each file at
    // its peaks as the backstop. Kept here only for run analytics.
    const reachable = useReachableLoudness(filteredLogs, loudnessLevel);
    const shortCount = (changeLoudness && reachable) ? reachable.shortRows.length : 0;

    // ---- Multi-row selection (Finder semantics, shared hook) ----
    // Only real track rows are selectable — message lines have no filePath.
    const isTrackLog = (log) => !!(log?.filePath && log.status !== 'summary' && log.status !== 'separator' && log.status !== 'folder-header');

    // Name/Folders view toggle — folder sources only (see FixMyTracksTab).
    const folderSource = isFolderSource(loudnessFiles);
    const labelFor = useDisplayLabel(enrichedMap);
    const displayRows = React.useMemo(() => {
        const trackPathOf = (row) => (isTrackLog(row) ? row.filePath : null);
        if (folderSource) {
            if (listGrouping !== 'folder') return filteredLogs; // already label-ordered by pre-populate
            return groupRowsByFolder(filteredLogs, loudnessFiles[0], trackPathOf, labelFor);
        }
        // Playlist/library sources: 'added' keeps the source's own order;
        // 'name' re-sorts at render. Picked files/drops have no toggle and
        // always keep pick order.
        if (!selectedPlaylistName || listSort !== 'name') return filteredLogs;
        return sortTrackRowsByLabel(filteredLogs, trackPathOf, labelFor);
    }, [filteredLogs, folderSource, listGrouping, listSort, loudnessFiles, labelFor, selectedPlaylistName]);

    const rowKeys = React.useMemo(() => {
        const seen = new Set();
        const keys = [];
        for (const log of displayRows) {
            if (isTrackLog(log) && !seen.has(log.filePath)) { seen.add(log.filePath); keys.push(log.filePath); }
        }
        return keys;
    }, [displayRows]);
    const keyIndex = React.useMemo(() => new Map(rowKeys.map((k, i) => [k, i])), [rowKeys]);

    // The visible rows in list order, as the player's local list context —
    // a track ending advances to the next row. Missing files stay out:
    // advancing into one would 404 and bounce the player around.
    const playableRowPaths = React.useMemo(() => {
        const seen = new Set();
        const paths = [];
        for (const log of displayRows) {
            if (!isTrackLog(log) || log.status === 'file-not-found') continue;
            if (log.filePath && !seen.has(log.filePath)) { seen.add(log.filePath); paths.push(log.filePath); }
        }
        return paths;
    }, [displayRows]);

    // If the visible order changes while one of THIS list's tracks is playing
    // (sort/grouping toggle, filters, rows still streaming in), re-register
    // the play-through context so "next" follows the CURRENT order — not the
    // order at the moment play was clicked.
    const orderKeyRef = React.useRef('');
    React.useEffect(() => {
        const key = playableRowPaths.join('\n');
        const prev = orderKeyRef.current;
        orderKeyRef.current = key;
        if (!currentlyPlayingPath || !prev || prev === key) return;
        // Anchor on the PLAYER's actual track when possible — the tab
        // highlight follows the player asynchronously, and re-registering on
        // a stale highlight right after an auto-advance would replay a row.
        const playerPath = localPathFromMusicSrc(window.musicPlayerInstance?.src);
        const anchor = (playerPath && playableRowPaths.includes(playerPath)) ? playerPath : currentlyPlayingPath;
        if (!playableRowPaths.includes(anchor)) return;
        setLocalListContext(dispatch, playableRowPaths, anchor, enrichedMap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playableRowPaths]);

    // Remove rows from the SESSION list only (log entries + the pending file
    // selection) — nothing is deleted from the local DB, so no undo toast.
    const removeFilesFromList = React.useCallback((paths, via) => {
        if (!paths?.length) return;
        const pathSet = new Set(paths);
        paths.forEach((p) => dispatch(removeLogsForFile(p)));
        dispatch(setLoudnessFiles(loudnessFiles.filter((f) => !pathSet.has(f))));
        track('tracks_removed_from_list', { tab: 'match_loudness', count: paths.length, via });
    }, [dispatch, loudnessFiles]);

    // Any change to the source value bumps the epoch — including dispatches
    // from OUTSIDE this tab (playlist sidebar "Open in tool"), which the
    // per-handler bumps can't see.
    const sourceFiles_watch = React.useRef(loudnessFiles);
    React.useEffect(() => {
        if (sourceFiles_watch.current !== loudnessFiles) {
            sourceFiles_watch.current = loudnessFiles;
            sourceEpochRef.current += 1;
        }
    }, [loudnessFiles]);

    const listContainerRef = React.useRef(null);
    const scrollRowIntoView = React.useCallback((index, key) => {
        listContainerRef.current
            ?.querySelector(`[data-rowkey="${CSS.escape(key)}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, []);
    const onDeleteSelected = React.useCallback((paths) => removeFilesFromList(paths, 'delete_key'), [removeFilesFromList]);
    const {
        selected: selectedPaths,
        orderedSelected,
        handleRowClick,
        selectOnly,
    } = useRowSelection({ keys: rowKeys, onDelete: onDeleteSelected, onArrowFocus: scrollRowIntoView, rowSelector: '.log-entry' });

    const handleRowContextMenu = (log, e) => {
        if (!isTrackLog(log)) return;
        e.preventDefault();
        const key = log.filePath;
        if (selectedPaths.size > 1 && selectedPaths.has(key)) {
            setBulkCtx({ x: e.clientX, y: e.clientY });
            return;
        }
        selectOnly(key, keyIndex.get(key));
        setRowCtx({ key, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    // Handlers
    const handleChooseFiles = async () => {
        setSelectedPlaylistName(null);
        setSelectedPlaylistRef(null);
        const chosenFiles = await window.electron.openFile(supportedFormats);
        sourceEpochRef.current += 1;
        if (chosenFiles?.length) dispatch(clearLogs());
        dispatch(setLoudnessFiles(chosenFiles || []));
    };

    const handleChooseDir = async () => {
        const chosenDir = await window.electron.openDir();
        if (!chosenDir || chosenDir.length === 0) return; // dialog canceled — keep current selection
        setSelectedPlaylistName(null);
        setSelectedPlaylistRef(null);
        sourceEpochRef.current += 1;
        dispatch(clearLogs());
        dispatch(setLoudnessFiles(chosenDir));
    };

    const handleChooseLibrary = async () => {
        try {
            const allTracks = await window.electron.db.tracks.getAll();
            const filePaths = (allTracks || [])
                .map(t => t.file_path || t.filePath || t.file)
                .filter(f => f && !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
            if (filePaths.length === 0) {
                toast.warning('Your library is empty'); return;
            }
            setSelectedPlaylistName('My Library');
            setSelectedPlaylistRef(null);
            sourceEpochRef.current += 1;
            dispatch(clearLogs());
            dispatch(setLoudnessFiles(filePaths));
        } catch (error) {
            console.error('Error loading library:', error);
            toast.error('Failed to load library');
        }
    };

    const handleResetFilePath = () => {
        setSelectedPlaylistName(null);
        setSelectedPlaylistRef(null);
        sourceEpochRef.current += 1;
        dispatch(resetLoudnessFilePath());
        dispatch(clearLogs());
    }

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files).map(file => window.electron.getPathForFile(file)).filter(Boolean);
        if (droppedFiles.length > 0) {
            // Dropped files replace the selection — the previous playlist (if
            // any) is no longer the source.
            setSelectedPlaylistName(null);
            setSelectedPlaylistRef(null);
            sourceEpochRef.current += 1;
            dispatch(clearLogs());
            dispatch(setLoudnessFiles(droppedFiles));
        }
    };

    const handleRefresh = () => {
        handleRunYourMagic(false); // Run analyze again - will update existing logs
    }

    // Auto-analyze when triggered from playlist sidebar
    React.useEffect(() => {
        // DESIGN V2: a loaded source analyzes on its own; the button is the write step.
        // Arm once per load: a new files array, or the same array loaded again
        // after its results were cleared (not during a run, which clears too).
        if (v2 && loudnessFiles.length > 0 && logs.length === 0 && !loudnessIsProcessing && !autoAnalyze
            && (v2AutoArmedRef.current?.files !== loudnessFiles || Date.now() - v2AutoArmedRef.current.at > 3000)) {
            v2AutoArmedRef.current = { files: loudnessFiles, at: Date.now() };
            dispatch(setLoudnessAutoAnalyze(true));
            return;
        }
        if (autoAnalyze && loudnessFiles.length > 0 && !loudnessIsProcessing) {
            dispatch(setLoudnessAutoAnalyze(false));
            handleRunYourMagic(false);
        }
    }, [autoAnalyze, loudnessFiles, logs.length, loudnessIsProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close playlist dropdown on outside click
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (playlistDropdownRef.current && !playlistDropdownRef.current.contains(e.target)) {
                setPlaylistDropdownOpen(false);
            }
        };
        if (playlistDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [playlistDropdownOpen]);

    const handlePlaylistDropdownToggle = async (anchorEl) => {
        if (playlistDropdownOpen) { setPlaylistDropdownOpen(false); return; }
        setPlaylistSearch('');
        if (anchorEl) {
            const rect = anchorEl.getBoundingClientRect();
            setPlaylistDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 250) });
        }
        setPlaylistDropdownOpen(true);
        setTimeout(() => playlistSearchRef.current?.focus(), 50);
        setPlaylistsLoading(true);
        try {
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                setPlaylistList(result?.data || []);
            } else {
                const allPlaylists = await window.electron.db.playlists.getAll(false);
                setPlaylistList(allPlaylists || []);
            }
        } catch (error) {
            console.error('Error loading playlists:', error);
            toast.error('Failed to load playlists');
        } finally {
            setPlaylistsLoading(false);
        }
    };

    const handlePlaylistSelect = async (playlist) => {
        setPlaylistDropdownOpen(false);
        setSourceResolving(true);
        try {
            let filePaths = [];
            let total = 0;
            let playlistRef = null;
            if (useServer) {
                // Cloud playlist — resolve rows to local files (exact link /
                // origin path, one-time heuristic backfill for legacy rows).
                const loaded = await loadCloudPlaylistFiles(accessToken, playlist.id);
                if (!loaded || loaded.total === 0) {
                    toast.warning('This playlist has no tracks'); return;
                }
                total = loaded.total;
                filePaths = loaded.filePaths;
                if (filePaths.length === 0) {
                    toast.warning('None of this playlist’s tracks are on this device — these tools work on local audio files');
                    return;
                }
                playlistRef = { source: 'cloud', id: playlist.id, rowIdsByPath: loaded.rowIdsByPath || {} };
            } else {
                const playlistData = await window.electron.db.playlists.get(playlist.id, true);
                if (!playlistData || !playlistData.tracks || playlistData.tracks.length === 0) {
                    toast.warning('This playlist has no tracks'); return;
                }
                const tracks = playlistData.tracks.filter(t => t.file || t.filePath);
                if (tracks.length === 0) { toast.warning('No valid file paths found in playlist'); return; }
                // Filter out cloud/online tracks — they don't have local files to analyze
                total = tracks.length;
                filePaths = tracks
                    .map(t => t.file || t.filePath)
                    .filter(f => !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
                if (filePaths.length === 0) {
                    toast.warning('This playlist only contains cloud tracks — no local files to analyze');
                    return;
                }
                playlistRef = { source: 'local', id: playlist.id };
            }
            setSelectedPlaylistName(playlist.name);
            setSelectedPlaylistRef(playlistRef);
            sourceEpochRef.current += 1;
            dispatch(clearLogs());
            dispatch(setLoudnessFiles(filePaths));
            const skipped = total - filePaths.length;
            if (skipped > 0) toast.info(`Skipped ${skipped} ${skipped === 1 ? 'track' : 'tracks'} with no local file on this device`);
            track('playlist_opened_in_tool', {
                tab: 'match-loudness',
                source: useServer ? 'cloud' : 'local',
                track_count: filePaths.length,
                skipped_count: skipped,
            });
        } catch (error) {
            console.error('Error loading playlist tracks:', error);
            toast.error('Failed to load playlist tracks');
        } finally {
            setSourceResolving(false);
        }
    };

    // DESIGN V2: the settings keep one of the two on; heal a state saved before that.
    React.useEffect(() => {
        if (v2 && !changeLoudness && !addLoudnessToFileNames) dispatch(setChangeLoudness(true));
    }, [v2, changeLoudness, addLoudnessToFileNames, dispatch]);

    const editFilenamesOnly = () => {
        return !changeLoudness && addLoudnessToFileNames;
    }

    const handleStopProcessing = () => {
        dispatch(setLoudnessCancelRequested(true));
        dispatch(setLoudnessIsProcessing(false));
        dispatch(clearPendingLogs()); // Cancelled rows must not linger as "Queued"
        window.electron.cancelLoudnessMatching(); // Implemented in backend
        // dispatch(addLogs([{ msg: '⛔ Processing cancelled', status: 'error' }]));
    };

    const handleRunYourMagic = async (fix) => {
        // Prevent multiple simultaneous analyses
        if (loudnessIsProcessing) {
            return;
        }
        // Source captured at click time; abort if it changes during any await
        // below (see sourceEpochRef).
        const runEpoch = sourceEpochRef.current;
        const sourceChanged = () => sourceEpochRef.current !== runEpoch;

        // Match acts on the VISIBLE rows — what you see is what runs (the
        // LUFS range scopes it). Analyze always re-scans the full source, and
        // a Match with no analyzed rows yet falls back to the raw source (old
        // behavior).
        const hasRows = logs.some((l) => l?.filePath && l.status !== 'summary' && l.status !== 'separator');
        const scopedFix = fix && hasRows;
        const runFiles = scopedFix ? rowKeys : loudnessFiles;
        if (scopedFix && runFiles.length === 0) return; // filters hide everything; button is disabled too

        // Filters are hiding rows the run could have matched — make skipping
        // them an explicit choice. No prompt when the view covers every row.
        if (scopedFix && hiddenMatchableCount > 0) {
            const proceed = await toast.confirm(
                `The LUFS filter is on — this will run on the ${visibleMatchableCount} track${visibleMatchableCount === 1 ? '' : 's'} in view and skip ${hiddenMatchableCount} hidden one${hiddenMatchableCount === 1 ? '' : 's'}. Widen the range to include all ${matchableCount}.`,
                'Loudness filter is active',
                { confirmText: visibleMatchableCount === 1 ? 'Run on 1 track' : `Run on ${visibleMatchableCount} tracks`, confirmVariant: 'primary' }
            );
            if (!proceed) return;
        }

        const effectiveTarget = loudnessLevel;

        // Sources so quiet that reaching the target would amplify hiss more
        // than music. Unlike silent files (always refused — their loudness is
        // unknowable) this is a QUALITY call about the user's own material,
        // so it's their decision: skip them, or match anyway. Asked BEFORE
        // the run so the button's count never lies about what will happen.
        let allowExtremeBoost = false;
        const extremeAtTarget = scopedFix
            ? filteredLogs.filter(l => l?.status === 'analyze-loudness' && needsExtremeBoost(l.loudness, effectiveTarget))
            : [];
        if (scopedFix && !editFilenamesOnly() && extremeAtTarget.length > 0) {
            const n = extremeAtTarget.length;
            const biggest = Math.max(...extremeAtTarget.map(l => matchGainDb(l.loudness, effectiveTarget) || 0));
            const choice = await toast.confirm(
                `${n} track${n === 1 ? '' : 's'} would need up to +${biggest.toFixed(0)}dB to reach ${effectiveTarget} LUFS. `
                + `That much boost amplifies hiss and noise along with the music, so the copies usually sound worse than the originals.`,
                n === 1 ? 'One track is very quiet' : 'Some tracks are very quiet',
                {
                    confirmText: n === 1 ? 'Skip it' : 'Skip them',
                    confirmVariant: 'primary',
                    secondaryText: 'Match anyway',
                }
            );
            if (choice === false) return;            // Cancel — run nothing
            allowExtremeBoost = choice === 'secondary';
        }

        // Time-based large-selection gate (silent for small sets) — applies to
        // library, playlist, folder and dropped files alike.
        const fileCount = await countAudioFiles(runFiles);
        const sourceLabel = selectedPlaylistName === 'My Library' ? 'library'
            : selectedPlaylistName ? 'playlist'
            : (loudnessFiles.length === 1 && fileCount !== 1) ? 'folder' : 'selection';
        if (!(await confirmLargeSet(toast, fileCount, {
            secondsPerFile: SET_COST.loudness,
            parallelism: Math.max(1, concurrencyLevel || 1),
            sourceLabel,
            tab: 'match_loudness',
        }))) return;
        if (sourceChanged()) return;

        window.electron.analytics.track('loudness_match_run', {
            mode: fix ? 'fix' : 'analyze',
            ...(scopedFix ? { visible_track_count: runFiles.length, hidden_matchable_count: hiddenMatchableCount } : {}),
            ...(extremeAtTarget.length > 0 && scopedFix
                ? { extreme_boost_count: extremeAtTarget.length, extreme_boost_choice: allowExtremeBoost ? 'match_anyway' : 'skip' }
                : {}),
            ...(scopedFix && shortCount > 0 ? { short_of_target_count: shortCount } : {}),
        });
        dispatch(setLoudnessIsProcessing(true));
        dispatch(setLoudnessProgress(0));
        // Analyze rebuilds the list; Fix updates the analyzed rows IN PLACE
        // (produced files replace their original's row via oldFilePath) —
        // same rule as the Compatibility tab.
        if (!fix) {
            dispatch(clearLogs());
            // Pre-populate every row in SOURCE order (playlist position /
            // library sort / dialog selection; folders sort by the DISPLAYED
            // "Artist - Title" label) so the list never reorders as results
            // stream in — each result updates its "pending" row in place
            // (updateOrAddLogs), and whatever the run never reports on is
            // dropped at completion (clearPendingLogs).
            try {
                const ordered = await resolveOrderedAudioFiles(runFiles, {
                    includeSubDirs: enableRecursive,
                    labelFor: (p) => getEnrichedParts(enrichedMap, p).label,
                });
                if (sourceChanged()) { dispatch(setLoudnessIsProcessing(false)); return; }
                if (ordered.length > 0) {
                    dispatch(setLogs(ordered.map((f) => ({
                        filePath: f,
                        msg: f.split(/[\\/]/).pop(),
                        status: 'pending',
                    }))));
                }
            } catch { /* enumeration failed — rows just append as they complete */ }
        }

        const startTime = Date.now(); // Record the start time

        // old → new paths this fix produced (renames and copies alike) —
        // used to repoint the session list so Refresh re-analyzes the outcome.
        const producedPaths = {};

        // This function will update progress based on backend reporting
        const updateProgressFromBackend = async ({progressPercentage, logs, notice, pendingDjUpdates, matchSummary}) => {
            // DJ-library sync notice (e.g. "Rekordbox is open — close it and re-run").
            if (notice) toast.warning(notice, 9000);
            if (fix && logs) {
                for (const log of logs) {
                    if (log.oldFilePath && log.filePath && log.oldFilePath !== log.filePath) {
                        producedPaths[log.oldFilePath] = log.filePath;
                    }
                }
            }
            // Apply this batch's rows BEFORE the completion branch: the final
            // file's log arrives in the SAME callback that reports 100%, and
            // clearPendingLogs below must not drop its still-pending row (that
            // re-added the last track at the top of the list).
            dispatch(updateOrAddLogs(logs));
            // Keep-originals copies are an opt-in commit: nothing references
            // them until the user clicks "Use fixed files" — sticky toast,
            // shared with the Compatibility tab (see fixedCopiesCommit.js).
            if (pendingDjUpdates?.length) {
                offerFixedCopiesCommit({
                    toast,
                    source: 'loudness',
                    pendingDjUpdates,
                    noun: 'loudness-matched',
                    playlistRef: selectedPlaylistRef,
                    playlistName: selectedPlaylistName,
                    accessToken,
                    repointSession: (byOld) => dispatch(setLoudnessFiles(loudnessFiles.map((f) => byOld[f] || f))),
                });
            }
            if (progressPercentage >= 100 &&
                ((logs && logs[0].status === 'summary') || logs?.[0]?.oneFile)) {
                await waitRemaining(startTime); // Keep progress visible briefly so fast runs don't flash-complete (see minVisibleDelay)
                dispatch(setLoudnessProgress(progressPercentage));
                dispatch(setLoudnessIsProcessing(false));  // Processing is done
                dispatch(clearPendingLogs());              // Rows the walker never reached
                // In-place runs (renames) are auto-committed — the old path is
                // gone, so the session list must follow the produced files.
                // Keep-originals copies repoint on the "Use fixed files" click.
                if (!backupLoudnessOriginalFiles && Object.keys(producedPaths).length > 0) {
                    dispatch(setLoudnessFiles(loudnessFiles.map((f) => producedPaths[f] || f)));
                }
                // A run where most/all files were skipped looks like a silent
                // failure — say WHY nothing (or little) happened. Skipped =
                // the target copy already exists, the file IS that copy, or a
                // sibling variant covers it.
                if (matchSummary?.skipped > 0) {
                    const { created = 0, skipped } = matchSummary;
                    toast.info(created === 0
                        ? `No copies created — every file already has a ${loudnessLevel} LUFS copy (enable "Overwrite existing fixed files" in Settings to re-create them)`
                        : `${skipped} file${skipped > 1 ? 's' : ''} skipped — a ${loudnessLevel} LUFS copy already exists or covers them`,
                    12000);
                }
                // Separate from the "already covered" skip above: these were
                // REFUSED because matching them would wreck the audio (silent
                // sources, or a boost big enough to amplify only noise).
                // Succeeded, but below target: a bigger boost would have
                // clipped. Distinct from the refusals below — these DID produce
                // files, so say what they actually got instead of the target.
                if (matchSummary?.peakLimited > 0) {
                    const n = matchSummary.peakLimited;
                    toast.info(
                        `${n} file${n > 1 ? 's' : ''} matched below ${loudnessLevel} LUFS — their peaks were already too close to full scale to boost further without clipping`,
                        12000
                    );
                }
                // Refused for the opposite reason to `unmatchable`: already
                // as loud as they can safely go.
                if (matchSummary?.atCeiling > 0) {
                    const n = matchSummary.atCeiling;
                    toast.warning(
                        `${n} file${n > 1 ? 's' : ''} left untouched — already peaking near full scale, so ${n > 1 ? 'they' : 'it'} can't be made louder without clipping`,
                        12000
                    );
                }
                if (matchSummary?.unmatchable > 0) {
                    const n = matchSummary.unmatchable;
                    toast.warning(
                        `${n} file${n > 1 ? 's' : ''} left untouched — silent or far too quiet to reach ${loudnessLevel} LUFS without amplifying noise`,
                        12000
                    );
                }
                track('loudness_match_run_complete', {
                    success: true,
                    track_count: loudnessFiles.length,
                    duration_ms: Date.now() - startTime,
                    ...(matchSummary ? {
                        copies_created: matchSummary.created,
                        skipped_existing: matchSummary.skipped,
                        skipped_unmatchable: matchSummary.unmatchable,
                        peak_limited: matchSummary.peakLimited,
                        skipped_at_ceiling: matchSummary.atCeiling,
                    } : {}),
                });
            } else {
                dispatch(setLoudnessProgress(progressPercentage));
            }
        };

        if (sourceChanged()) { dispatch(setLoudnessIsProcessing(false)); return; }
        // Call the backend function, passing in the directory and progress callback
        window.electron.matchAllFilesInDir(
            runFiles,
            // Per-file ticks are folded into a few applies per second (see
            // utils/progressBatcher.js); the final tick flushes then applies.
            batchProgressTicks(updateProgressFromBackend),
            enableRecursive,
            backupLoudnessOriginalFiles,
            concurrencyLevel,
            logOnlyChanges,
            fix,
            overwriteExistingFixedFiles,
            effectiveTarget,
            editFilenamesOnly(),
            allowExtremeBoost,
            addLoudnessToFileNames
        );
    };

    const handlePlayClick = async (log, e) => {
        e.stopPropagation();

        if (!log.filePath || log.status === 'summary') {
            return;
        }

        try {
            const audioPath = log.filePath;

            if (currentlyPlayingPath === audioPath && !isPlaying) {
                if (window.musicPlayerInstance) {
                    window.musicPlayerInstance.play();
                }
                // Re-assert the list context (see FixMyTracksTab): resuming
                // must restore play-through-the-list, not solo play.
                setLocalListContext(dispatch, playableRowPaths, audioPath, enrichedMap);
                setIsPlaying(true);
                dispatch(setAutoPlay(true));
                return;
            }

            // Prefer enriched (Mixpedia/ID3) title/artist/cover over the filename.
            const { title: trackName, artist, cover: enrichedCover } = getEnrichedParts(enrichedMap, audioPath);

            const musicSrc = await window.electron.getAudioUrl(audioPath);

            // Fall back to embedded album art only when we have no enriched cover.
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(audioPath);
            } catch (err) {
            }

            const newTrack = {
                name: trackName,
                singer: artist,
                cover: enrichedCover || albumArtUrl,
                musicSrc
            };

            dispatch(resetAudioList());
            dispatch(addTrack(newTrack));
            // Register the visible rows as the player's list context so
            // playback continues down the list when this track ends —
            // auto-radio only takes over after the LAST row (same behavior
            // as the Library tab).
            setLocalListContext(dispatch, playableRowPaths, audioPath, enrichedMap);
            dispatch(setAutoPlay(true));

            setCurrentlyPlayingPath(audioPath);
            setIsPlaying(true);
        } catch (error) {
            console.error('Error playing track:', error);
        }
    };

    const handlePauseClick = (e) => {
        e.stopPropagation();
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
        }
        setIsPlaying(false);
        dispatch(setAutoPlay(false));
    };

    const handleStopClick = (e) => {
        e.stopPropagation();
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
            window.musicPlayerInstance.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentlyPlayingPath(null);
        dispatch(setAutoPlay(false));
        dispatch(resetAudioList());
    };

    const handleAddToQueue = async (log, e) => {
        e.stopPropagation();

        if (!log.filePath || log.status === 'summary' || log.status === 'separator') {
            return;
        }

        try {
            const audioPath = log.filePath;
            const musicSrc = await window.electron.getAudioUrl(audioPath);

            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(audioPath);
            } catch (err) {
            }

            // Prefer enriched (Mixpedia/ID3) title/artist/cover over the filename.
            const { title: trackName, artist, cover: enrichedCover } = getEnrichedParts(enrichedMap, audioPath);

            const newTrack = {
                name: trackName,
                singer: artist,
                cover: enrichedCover || albumArtUrl,
                musicSrc
            };

            dispatch(addTrack(newTrack));
        } catch (error) {
            console.error('Error adding track to queue:', error);
        }
    };

    // Latest handlers for the memoized rows (see LoudnessLogRow). The row's
    // `api` prop never changes identity; each method reads the current
    // closure through this ref, so rows don't re-render on every tab render.
    const rowHandlersRef = React.useRef({});
    const rowApi = React.useMemo(() => ({
        rowClick: (log, e) => rowHandlersRef.current.rowClick(log, e),
        rowContextMenu: (log, e) => rowHandlersRef.current.rowContextMenu(log, e),
        playClick: (log, e) => rowHandlersRef.current.playClick(log, e),
        pauseClick: (e) => rowHandlersRef.current.pauseClick(e),
        addToQueue: (log, e) => rowHandlersRef.current.addToQueue(log, e),
        removeFromList: (paths, via) => rowHandlersRef.current.removeFromList(paths, via),
    }), []);
    rowHandlersRef.current = {
        rowClick: (log, e) => handleRowClick(keyIndex.get(log.filePath), log.filePath, e),
        rowContextMenu: handleRowContextMenu,
        playClick: handlePlayClick,
        pauseClick: handlePauseClick,
        addToQueue: handleAddToQueue,
        removeFromList: removeFilesFromList,
    };

    // Shared by both designs: the Open menu (Files / Folder / Playlist / My
    // Library — same ids and .dropdown-item hooks the QA matrix drives) and
    // the playlist picker it opens.
    const sourceLabel = loudnessFiles.length === 0
        ? 'Open'
        : selectedPlaylistName
            ? selectedPlaylistName
            : loudnessFiles.length > 1
                ? `${loudnessFiles.length} files`
                : (loudnessFiles[0].split('/').pop() || loudnessFiles[0]);
    const openMenu = (
        <Dropdown>
            <Dropdown.Toggle
                className={v2 ? 'v2-btn v2-source-chip' : 'button choose-button'}
                id="open-dropdown"
                ref={playlistBtnRef}
                title={v2 ? (loudnessFiles.length === 1 ? loudnessFiles[0] : (selectedPlaylistName ? `${loudnessFiles.length} files` : '')) : undefined}
            >
                {v2 ? (
                    <>
                        <DirectoryIcon className="icon" />
                        <span className="v2-source-label">{sourceLabel}</span>
                        {selectedPlaylistName && loudnessFiles.length > 0 && <span className="v2-faint v2-num">· {loudnessFiles.length}</span>}
                    </>
                ) : (
                    <><OpenIcon className="icon" />Open</>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={handleChooseFiles}>Files</Dropdown.Item>
                <Dropdown.Item onClick={handleChooseDir}>Folder</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handlePlaylistDropdownToggle(playlistBtnRef.current)}>Playlist</Dropdown.Item>
                <Dropdown.Item onClick={handleChooseLibrary}>My Library</Dropdown.Item>
                {v2 && loudnessFiles.length > 0 && (
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => handleResetFilePath()}>Clear</Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
    const playlistPicker = playlistDropdownOpen && (
        <div ref={playlistDropdownRef} className="smart-recs-dropdown-menu" style={{ position: 'fixed', top: playlistDropdownPos.top, left: playlistDropdownPos.left, width: playlistDropdownPos.width, zIndex: 1000 }}>
            <div className="dropdown-search-wrapper">
                <svg className="dropdown-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input ref={playlistSearchRef} className="dropdown-search-input" type="text" placeholder="Search playlists..." value={playlistSearch} onChange={(e) => setPlaylistSearch(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
            </div>
            <div className="dropdown-options">
                {playlistsLoading ? (
                    <div className="dropdown-skel" aria-label="Loading playlists">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="dropdown-skel-option"><span /></div>)}</div>
                ) : (
                    <>
                        {playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).map((pl) => (
                            <button key={pl.id} className="dropdown-option" onClick={() => handlePlaylistSelect(pl)}>
                                <span className="dropdown-option-name">{pl.name}</span>
                            </button>
                        ))}
                        {playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).length === 0 && (
                            playlistList.length === 0 ? (
                            <div className="dropdown-no-results" style={{ whiteSpace: 'normal', lineHeight: 1.4, textAlign: 'left', padding: '10px 12px' }}>
                                No playlists yet. These tools work on audio files on this computer — import a library (Rekordbox, Serato, Traktor, VirtualDJ) or add local tracks.
                            </div>
                        ) : (
                            <div className="dropdown-no-results">No playlists match your search</div>
                        )
                        )}
                    </>
                )}
            </div>
        </div>
    );

    // ---- DESIGN V2 header pieces ----
    const v2Stats = useMemo(() => (v2 ? computeLoudnessStats(logs, loudnessLevel) : null), [v2, logs, loudnessLevel]);
    const hasAnalyzed = matchableCount > 0 || logs.some((l) => l?.filePath && l.status !== 'pending');
    const matchDisabled = loudnessIsProcessing || sourceResolving || loudnessFiles.length === 0
        || (!changeLoudness && !addLoudnessToFileNames) || (matchableCount > 0 && visibleMatchableCount === 0);
    const matchTitle = !changeLoudness && !addLoudnessToFileNames
        ? 'Turn on "Match loudness to target" or "Add loudness to filenames" in Settings'
        : (matchableCount > 0 && visibleMatchableCount === 0)
            ? `Nothing in the current view — ${matchableCount} analyzed track${matchableCount === 1 ? ' is' : 's are'} hidden by the filter`
            : '';
    // Filter chips drive the same LUFS range the slider does (so "what you
    // see is what runs" keeps holding): too loud = above target, too quiet =
    // below target, all = the full range.
    const target = Number(loudnessLevel);
    const chipFilter = (loudnessFilterMin <= -70 && loudnessFilterMax >= 0) ? 'all'
        : (loudnessFilterMin > target - 0.01 && loudnessFilterMax >= 0) ? 'loud'
            : (loudnessFilterMin <= -70 && loudnessFilterMax < target + 0.01) ? 'quiet' : 'custom';
    const setChipFilter = (which) => {
        const min = which === 'loud' ? Number((target + 0.1).toFixed(1)) : -70;
        const max = which === 'quiet' ? Number((target - 0.1).toFixed(1)) : 0;
        dispatch(setLoudnessFilterMin(min));
        dispatch(setLoudnessFilterMax(max));
        track('list_grouping_changed', { tab: 'match_loudness', grouping: `lufs_${which}` });
    };
    const { loudCount, quietCount } = useMemo(() => {
        let loud = 0; let quiet = 0;
        for (const l of logs) {
            if (l?.status !== 'analyze-loudness' || typeof l.loudness !== 'number') continue;
            if (l.loudness > target + 0.05) loud++; else if (l.loudness < target - 0.05) quiet++;
        }
        return { loudCount: loud, quietCount: quiet };
    }, [logs, target]);
    const commitTarget = () => {
        const n = Number(inputLoudnessLevel);
        if (inputLoudnessLevel === '' || isNaN(n) || n > 0 || n < -60) { dispatch(setInputLoudnessLevel(loudnessLevel)); return; }
        dispatch(setLoudnessLevel(n));
    };
    const v2Header = v2 && (
        <ToolHeader
            className="v2-tool-loudness"
            source={<>{openMenu}{playlistPicker}</>}
            stats={loudnessIsProcessing ? (
                <ProgressBarWithStop isProcessing={loudnessIsProcessing} progress={loudnessProgress} handleStopProcessing={handleStopProcessing} v2 total={logs.filter((l) => l?.filePath).length} />
            ) : loudnessFiles.length === 0 ? (
                null /* the empty page below already says it; repeating it here read as a stray line */
            ) : v2Stats && v2Stats.totalAudio > 0 ? (
                <>
                    <b>{v2Stats.totalAudio}</b> analyzed · <b>{v2Stats.matched}</b> at target
                    {v2Stats.hasNumericLoudness && <> · range <b>{formatToNegativeDecimal(v2Stats.lowest)}</b> to <b>{formatToNegativeDecimal(v2Stats.highest)}</b> LUFS</>}
                    {v2Stats.unmatchable > 0 && <span title="Files a match can't help: silent or extremely quiet (boosting would amplify noise), or already peaking near full scale"> · <b>{v2Stats.unmatchable}</b> can't match</span>}
                    {hiddenMatchableCount > 0 && <span title="Match runs on the visible rows only — widen the filter to include these"> · <b>{hiddenMatchableCount}</b> filtered out</span>}
                </>
            ) : (
                <><b>{loudnessFiles.length}</b> {loudnessFiles.length === 1 && !selectedPlaylistName ? 'item' : 'files'} · not analyzed yet</>
            )}
            settings={(
                <>
                    <div className="v2-popover-title">Settings</div>
                    {/* One of the two always stays on, so Match never turns into a dead button.
                        The target lives in the row above the list, its range filter in the chips. */}
                    <PopoverRow label="Match loudness to target"><ToggleSwitch id="v2-changeLoudness" checked={!!changeLoudness} onChange={(v) => { dispatch(setChangeLoudness(v)); if (!v && !addLoudnessToFileNames) dispatch(setAddLoudnessToFileNames(true)); }} label="" /></PopoverRow>
                    <PopoverRow label="Add loudness to filenames" hint="Appends e.g. [-9 LUFS] to the name"><ToggleSwitch id="v2-addLoudnessToFileNames" checked={!!addLoudnessToFileNames} onChange={(v) => { dispatch(setAddLoudnessToFileNames(v)); if (!v && !changeLoudness) dispatch(setChangeLoudness(true)); }} label="" /></PopoverRow>
                    <PopoverRow label="Keep original files" hint="On: a matched copy next to each original. Off: re-encode in place."><ToggleSwitch id="v2-backupLoudnessOriginalFiles" checked={!!backupLoudnessOriginalFiles} onChange={(v) => dispatch(setBackupLoudnessOriginalFiles(v))} label="" /></PopoverRow>
                </>
            )}
            actions={hasAnalyzed ? (
                <>
                    <button type="button" className="v2-btn analyze-button" onClick={() => handleRunYourMagic(false)}
                        disabled={loudnessIsProcessing || sourceResolving || loudnessFiles.length === 0}>
                        Analyze again
                    </button>
                    <button type="button" className="v2-btn primary" aria-disabled={matchDisabled ? 'true' : undefined} title={matchTitle}
                        onClick={() => { if (matchDisabled) return; if (!canAccess) { requireUpgrade(); return; } handleRunYourMagic(true); }}>
                        {editFilenamesOnly() ? 'Edit filenames' : 'Match loudness'}{!loudnessIsProcessing && visibleMatchableCount > 0 ? ` (${visibleMatchableCount})` : ''}
                    </button>
                </>
            ) : loudnessFiles.length > 0 && !loudnessIsProcessing ? (
                <button type="button" className="v2-btn primary analyze-button" onClick={() => handleRunYourMagic(false)}
                    disabled={loudnessIsProcessing || sourceResolving || loudnessFiles.length === 0}
                    title={loudnessFiles.length === 0 ? 'Open files, a folder or a playlist first' : ''}>
                    Analyze
                </button>
            ) : null}
        />
    );
    const v2FilterRow = v2 && loudnessFiles.length > 0 && (
        <div className="v2-filter-row">
            <span className="v2-faint">Show</span>
            <button type="button" className={`v2-chip sm${chipFilter === 'all' ? ' on' : ''}`} onClick={() => setChipFilter('all')}>All{matchableCount > 0 && <small>· {matchableCount}</small>}</button>
            <button type="button" className={`v2-chip sm${chipFilter === 'loud' ? ' on' : ''}`} onClick={() => setChipFilter('loud')} title="Louder than the target">Too loud<small>· {loudCount}</small></button>
            <button type="button" className={`v2-chip sm${chipFilter === 'quiet' ? ' on' : ''}`} onClick={() => setChipFilter('quiet')} title="Quieter than the target">Too quiet<small>· {quietCount}</small></button>
            {chipFilter === 'custom' && <span className="v2-chip sm on" title="A custom LUFS range from an earlier session: pick All to clear it">Custom range</span>}
            <span className="v2-tool-spacer" />
            {rowKeys.length > 0 && (folderSource ? (
                <span className="v2-seg" role="group" aria-label="List order">
                    <button type="button" className={listGrouping !== 'folder' ? 'on' : ''} title="Flat list, A-Z by track name"
                        onClick={() => { dispatch(setListGrouping('name')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'name' }); }}>Name</button>
                    <button type="button" className={listGrouping === 'folder' ? 'on' : ''} title="Grouped by subfolder, like your own folder structure"
                        onClick={() => { dispatch(setListGrouping('folder')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'folder' }); }}>Folders</button>
                </span>
            ) : selectedPlaylistName ? (
                <span className="v2-seg" role="group" aria-label="List order">
                    <button type="button" className={listSort !== 'name' ? 'on' : ''} title="The source's own order: playlist position or library sort"
                        onClick={() => { dispatch(setListSort('added')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'added' }); }}>Added</button>
                    <button type="button" className={listSort === 'name' ? 'on' : ''} title="A-Z by track name"
                        onClick={() => { dispatch(setListSort('name')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'name' }); }}>Name</button>
                </span>
            ) : null)}
            <span className="v2-faint">Target</span>
            <label className={`v2-chip sm v2-target-chip${changeLoudness ? '' : ' off'}`} title={changeLoudness ? 'Target loudness (LUFS)' : 'Turn on "Match loudness to target" in Settings to use a target'}>
                <input
                    type="number" min="-60" max="0" step="0.1"
                    value={inputLoudnessLevel}
                    onChange={(e) => dispatch(setInputLoudnessLevel(e.target.value))}
                    onBlur={commitTarget}
                    onKeyDown={(e) => { if (e.key === 'Enter') { commitTarget(); e.currentTarget.blur(); } e.stopPropagation(); }}
                    aria-label="Target loudness"
                />
                <small>LUFS</small>
            </label>
            {shortCount > 0 && (
                <button type="button" className="v2-btn quiet sm" title={`${shortCount === 1 ? '1 track' : `${shortCount} tracks`} can't reach ${loudnessLevel} LUFS without clipping. ${reachable.lufs} LUFS is the loudest all of them can share.`}
                    onClick={() => { dispatch(setLoudnessLevel(reachable.lufs)); dispatch(setInputLoudnessLevel(String(reachable.lufs))); track('loudness_target_suggestion_applied', { from: 'hint', level: reachable.lufs, track_count: reachable.trackCount }); }}>
                    Use {reachable.lufs}
                </button>
            )}
        </div>
    );

    return (
        <div
            className={`tab-container ${v2 ? 'v2-tool-page v2-loudness-page ' : ''}${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {v2Header}
            {!v2 && <div className="upper-part source-bar-header">
                <div className="source-bar">
                    <div className="source-bar-left">
                        {loudnessIsProcessing ? (
                            <ProgressBarWithStop
                                isProcessing={loudnessIsProcessing}
                                progress={loudnessProgress}
                                handleStopProcessing={handleStopProcessing}
                            />
                        ) : (
                            <>
                                <DirectoryIcon className="source-bar-icon" />
                                <span className={`source-bar-title${loudnessFiles.length === 0 ? ' empty' : ''}`} title={loudnessFiles.length === 1 ? loudnessFiles[0] : ''}>
                                    {loudnessFiles.length === 0
                                        ? 'Click "Open" or drop files here'
                                        : selectedPlaylistName
                                            ? selectedPlaylistName
                                            : loudnessFiles.length > 1
                                                ? `${loudnessFiles.length} Files`
                                                : (loudnessFiles[0].split('/').pop() || loudnessFiles[0])}
                                </span>
                                {selectedPlaylistName && loudnessFiles.length > 0 && <span className="source-bar-meta">· {loudnessFiles.length} files</span>}
                                {loudnessFiles.length > 0 && (
                                    <button className="source-bar-clear" onClick={() => handleResetFilePath()} aria-label="Clear" title="Clear">&times;</button>
                                )}
                            </>
                        )}
                    </div>
                    <div className="source-bar-actions">
                        {logs.length > 0 && (
                            <button className="source-bar-icon-btn" onClick={() => handleRefresh()} title="Refresh" aria-label="Refresh"><RefreshIcon className="icon" /></button>
                        )}
                        {openMenu}
                        {playlistPicker}
                        <Button className="button analyze-button main-button" onClick={() => handleRunYourMagic(false)}
                                disabled={loudnessIsProcessing || sourceResolving || loudnessFiles.length === 0}>
                            <AnalyzeFilesIcon className="icon"/>
                            Analyze
                        </Button>
                        <Button className="button main-button" onClick={() => { if (!canAccess) { requireUpgrade(); return; } handleRunYourMagic(true); }}
                                disabled={loudnessIsProcessing || sourceResolving || loudnessFiles.length === 0 || (!changeLoudness && !addLoudnessToFileNames) || (matchableCount > 0 && visibleMatchableCount === 0)}
                                title={matchableCount > 0 && visibleMatchableCount === 0 ? `Nothing in the current view — ${matchableCount} analyzed track${matchableCount === 1 ? ' is' : 's are'} hidden by filters` : ''}>
                            {editFilenamesOnly() ? (
                                <>
                                    <EditFilenamesIcon className="icon" />
                                    Edit Filenames{!loudnessIsProcessing && visibleMatchableCount > 0 ? ` (${visibleMatchableCount})` : ''}
                                </>
                            ) : (
                                <>
                                    <MatchLoudnessIcon className="icon" />
                                    Match Loudness{!loudnessIsProcessing && visibleMatchableCount > 0 ? ` (${visibleMatchableCount})` : ''}
                                </>
                            )}
                            
                        </Button>
                    </div>
                </div>
                {matchDisabledReason && (
                    <div className="loudness-action-hint" role="status">{matchDisabledReason}</div>
                )}
            </div>}
            <div className="lower-part">
                <div className="log-container">
                    <div className="files-container">
                        {loudnessFiles.length === 0 ? (
                        <div className="ftx-empty-state" onClick={handleChooseDir} role="button" tabIndex={0}>
                            <DirectoryIcon className="ftx-empty-icon" />
                            <div className="ftx-empty-title">Drop tracks here to match loudness</div>
                            <div className="ftx-empty-sub">or click to load files, a folder, or a playlist</div>
                        </div>
                        ) : (<>
                        {v2FilterRow}
                        {!v2 && <div className="file-summary-container">
                            <div className="directory-container log-entry">
                                <DirectoryIcon className="icon"/>
                                <span>{selectedPlaylistName ? `Playlist: ${selectedPlaylistName} (${loudnessFiles.length} tracks)` : `Directory: ${loudnessFiles.length > 0 ? (loudnessFiles.length > 1 ? `${loudnessFiles.length} Files Selected` : formatPath(loudnessFiles[0])) : 'No directory selected'}`}</span>
                                {rowKeys.length > 0 && (folderSource ? (
                                    <span className="list-grouping-toggle" role="group" aria-label="List order">
                                        <button type="button" className={listGrouping !== 'folder' ? 'active' : ''} title="Flat list, A-Z by track name"
                                            onClick={() => { dispatch(setListGrouping('name')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'name' }); }}>Name</button>
                                        <span className="lg-sep">·</span>
                                        <button type="button" className={listGrouping === 'folder' ? 'active' : ''} title="Grouped by subfolder, like your own folder structure"
                                            onClick={() => { dispatch(setListGrouping('folder')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'folder' }); }}>Folders</button>
                                    </span>
                                ) : selectedPlaylistName ? (
                                    <span className="list-grouping-toggle" role="group" aria-label="List order">
                                        <button type="button" className={listSort !== 'name' ? 'active' : ''} title="The source's own order: playlist position or library sort"
                                            onClick={() => { dispatch(setListSort('added')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'added' }); }}>Added</button>
                                        <span className="lg-sep">·</span>
                                        <button type="button" className={listSort === 'name' ? 'active' : ''} title="A-Z by track name"
                                            onClick={() => { dispatch(setListSort('name')); track('list_grouping_changed', { tab: 'match_loudness', grouping: 'name' }); }}>Name</button>
                                    </span>
                                ) : null)}
                            </div>
                            <LogSummaryStats logs={logs} loudnessLevel={loudnessLevel} formatToNegativeDecimal={formatToNegativeDecimal} hiddenByFilter={hiddenMatchableCount} />
                        </div>}
                        <div className="file-list-container" ref={listContainerRef}>
                            {logs.length > 0 && filteredLogs.length === 0 && logs.some(log => log?.msg?.includes('No audio files')) ? (
                                <div className="no-files-message">
                                    <AudioFileIcon className="no-files-icon" />
                                    <span>No audio files were found in folder</span>
                                </div>
                            ) : displayRows.length > 0 ? (
                                displayRows.map((log, index) => {
                                        if (log?.status === 'folder-header') {
                                            return (
                                                <div key={`h${index}`} className="log-entry folder-header">
                                                    <DirectoryIcon className="icon" />
                                                    <span>{log.folderLabel}</span>
                                                </div>
                                            );
                                        }
                                        const track = isTrackLog(log);
                                        const isCurrent = track && currentlyPlayingPath === log.filePath;
                                        return (
                                            <LoudnessLogRow
                                                key={track ? log.filePath : `i${index}`}
                                                log={log}
                                                enrichedMap={enrichedMap}
                                                selected={track && selectedPaths.has(log.filePath)}
                                                playing={isCurrent && isPlaying}
                                                ctxAt={rowCtx && rowCtx.key === log.filePath ? rowCtx : null}
                                                loudnessLevel={loudnessLevel}
                                                isLast={index === logs.length - 1}
                                                api={rowApi}
                                                v2={v2}
                                            />
                                        );
                                    })
                            ) : (
                                <div className="empty-log"></div>
                            )}
                        </div>
                        </>)}
                    </div>
                    {!v2 && <LoudnessSettings/>}
                </div>
            </div>
            {bulkCtx && orderedSelected.length > 1 && (
                <BulkRowContextMenu
                    x={bulkCtx.x}
                    y={bulkCtx.y}
                    onClose={() => setBulkCtx(null)}
                    analyticsFrom="match_loudness"
                    count={orderedSelected.length}
                    items={[
                        {
                            label: `Add ${orderedSelected.length} Tracks to Playlist`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>,
                            onClick: () => setBulkPlaylistTargets(orderedSelected),
                        },
                        { divider: true },
                        {
                            label: `Remove ${orderedSelected.length} Tracks from list`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>,
                            danger: true,
                            onClick: () => removeFilesFromList(orderedSelected, 'menu'),
                        },
                    ]}
                />
            )}
            {bulkPlaylistTargets.length > 0 && (
                <AddToPlaylistModal
                    show={true}
                    onHide={() => setBulkPlaylistTargets([])}
                    bulkFilePaths={bulkPlaylistTargets}
                    initialTab="add"
                    bulkLabel={`${bulkPlaylistTargets.length} tracks`}
                />
            )}
        </div>
    );
};

export default MatchLoudnessTab;
